(() => {
  /**
   * Contact page client-side validation + "Sending..." button state.
   * Backend remains Shopify native ({% form 'contact' %}).
   */
  const PHONE_PATTERN = /^\+?[0-9]{7,15}$/;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const getTrimmedValue = (element) => {
    if (!element) return '';
    return String(element.value ?? '').trim();
  };

  const getErrorMessage = ({ fieldKey, value }) => {
    if (fieldKey === 'name') {
      if (!value) return 'Name is required';
      if (value.length < 2) return 'Name must be at least 2 characters';
      if (value.length > 100) return 'Name must not exceed 100 characters';
      return '';
    }
    if (fieldKey === 'email') {
      if (!value) return 'Email is required';
      if (!EMAIL_PATTERN.test(value)) return 'Please enter a valid email address';
      return '';
    }
    if (fieldKey === 'phone') {
      if (!value) return 'Phone Number is required';
      if (!PHONE_PATTERN.test(value)) return 'Please enter a valid phone number (7–15 digits)';
      return '';
    }
    if (fieldKey === 'message') {
      if (!value) return 'Message is required';
      if (value.length < 10) return 'Message must be at least 10 characters';
      if (value.length > 1000) return 'Message must not exceed 1000 characters';
      return '';
    }
    return '';
  };

  const setFieldError = ({ fieldElement, errorElement, errorMessage }) => {
    const hasError = Boolean(errorMessage);
    if (fieldElement) {
      fieldElement.classList.toggle('is-error', hasError);
      fieldElement.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      const errorId = errorElement?.id;
      if (hasError && errorId) {
        fieldElement.setAttribute('aria-describedby', errorId);
      } else {
        fieldElement.removeAttribute('aria-describedby');
      }
    }
    if (errorElement) {
      if (hasError) {
        errorElement.textContent = errorMessage;
        errorElement.hidden = false;
      } else {
        errorElement.textContent = '';
        errorElement.hidden = true;
      }
    }
  };

  const validateContactForm = (formElement) => {
    const fieldElements = {
      name: formElement.querySelector('[data-contact-field="name"]'),
      email: formElement.querySelector('[data-contact-field="email"]'),
      phone: formElement.querySelector('[data-contact-field="phone"]'),
      message: formElement.querySelector('[data-contact-field="message"]')
    };

    const errorElements = {
      name: formElement.querySelector('[data-error-for="name"]'),
      email: formElement.querySelector('[data-error-for="email"]'),
      phone: formElement.querySelector('[data-error-for="phone"]'),
      message: formElement.querySelector('[data-error-for="message"]')
    };

    const values = {
      name: getTrimmedValue(fieldElements.name),
      email: getTrimmedValue(fieldElements.email),
      phone: getTrimmedValue(fieldElements.phone),
      message: getTrimmedValue(fieldElements.message)
    };

    const errors = {
      name: getErrorMessage({ fieldKey: 'name', value: values.name }),
      email: getErrorMessage({ fieldKey: 'email', value: values.email }),
      phone: getErrorMessage({ fieldKey: 'phone', value: values.phone }),
      message: getErrorMessage({ fieldKey: 'message', value: values.message })
    };

    setFieldError({ fieldElement: fieldElements.name, errorElement: errorElements.name, errorMessage: errors.name });
    setFieldError({ fieldElement: fieldElements.email, errorElement: errorElements.email, errorMessage: errors.email });
    setFieldError({ fieldElement: fieldElements.phone, errorElement: errorElements.phone, errorMessage: errors.phone });
    setFieldError({ fieldElement: fieldElements.message, errorElement: errorElements.message, errorMessage: errors.message });

    const firstInvalidKey = Object.keys(errors).find((key) => Boolean(errors[key]));
    if (firstInvalidKey) {
      fieldElements[firstInvalidKey]?.focus?.();
      return false;
    }
    return true;
  };

  const setSubmittingState = ({ formElement, isSubmitting }) => {
    const submitButton = formElement.querySelector('[data-contact-submit]');
    const submitLabel = formElement.querySelector('[data-contact-submit-label]');
    const submitIcon = formElement.querySelector('[data-contact-submit-icon]');
    if (!submitButton) return;
    submitButton.disabled = Boolean(isSubmitting);
    if (submitLabel) {
      submitLabel.textContent = isSubmitting ? 'Sending...' : submitLabel.getAttribute('data-original') || submitLabel.textContent;
    }
    if (submitIcon) {
      submitIcon.style.display = isSubmitting ? 'none' : '';
    }
  };

  const initializeContactForm = (formElement) => {
    const submitLabel = formElement.querySelector('[data-contact-submit-label]');
    if (submitLabel && !submitLabel.getAttribute('data-original')) {
      submitLabel.setAttribute('data-original', submitLabel.textContent || 'Send Message');
    }

    const onInput = (event) => {
      const target = event.target;
      if (!target?.matches?.('[data-contact-field]')) return;
      const fieldKey = target.getAttribute('data-contact-field');
      const errorElement = formElement.querySelector(`[data-error-for="${fieldKey}"]`);
      const value = getTrimmedValue(target);
      const errorMessage = getErrorMessage({ fieldKey, value });
      setFieldError({ fieldElement: target, errorElement, errorMessage });
    };

    formElement.addEventListener('input', onInput);
    formElement.addEventListener('blur', onInput, true);

    formElement.addEventListener('submit', (event) => {
      const isValid = validateContactForm(formElement);
      if (!isValid) {
        event.preventDefault();
        return;
      }
      setSubmittingState({ formElement, isSubmitting: true });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    const forms = document.querySelectorAll('form.gaurya-contact-form__form');
    forms.forEach((formElement) => initializeContactForm(formElement));
  });
})();

